import React from 'react';
import { Button, Checkbox, IndexTable, Text } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import type { Vehicle } from '../../app/models/Vehicle';
import type { FitmentFieldDefinition } from '../../app/models/FitmentCatalog';
import { getLegacyVehicleFieldValue, normalizeFitmentKey } from '../../app/utils/fitmentFields';

export const catalogColumns = (fields: FitmentFieldDefinition[]) => {
    const combined = fields.some(f => normalizeFitmentKey(f) === 'make')
        && fields.some(f => normalizeFitmentKey(f) === 'model');
    if (!combined) return fields;
    const identity = fields.find(f => normalizeFitmentKey(f) === 'model')!;
    return [identity, ...fields.filter(f => !['make', 'model'].includes(normalizeFitmentKey(f)))];
};

export const formatVehicleYears = (years: string[]) => {
    const sorted = Array.from(new Set(years)).sort((a, b) => Number(a) - Number(b));
    const ranges: string[] = [];
    for (let i = 0; i < sorted.length; i++) {
        const start = sorted[i];
        let end = start;
        while (i + 1 < sorted.length && /^\d{4}$/.test(end) && Number(sorted[i + 1]) === Number(end) + 1) {
            end = sorted[++i];
        }
        ranges.push(start === end ? start || '—' : `${start}–${end}`);
    }
    return ranges.join(', ');
};

type Props = {
    vehicles: Vehicle[];
    groups?: number[][] | null;
    fields: FitmentFieldDefinition[];
    selected: string[];
    onSelect: (id: string, checked: boolean) => void;
    onEdit: (vehicle: Vehicle) => void;
    onProducts: (vehicle: Vehicle) => void;
    expanded: Set<string>;
    onToggle: (key: string) => void;
};

export function VehicleCatalogRows({ vehicles, groups, fields, selected, onSelect, onEdit, onProducts, expanded, onToggle }: Props) {
    const byId = new Map(vehicles.map((v, index) => [v.id, { vehicle: v, index }]));
    const columns = catalogColumns(fields);
    const combined = columns.length < fields.length;
    const yearField = fields.find(f => normalizeFitmentKey(f) === 'year');
    const makeField = fields.find(f => normalizeFitmentKey(f) === 'make');
    const value = (v: Vehicle, field?: FitmentFieldDefinition) => field ? getLegacyVehicleFieldValue(v, field) : '';
    const label = (v: Vehicle) => fields.map(f => value(v, f)).filter(Boolean).join(' ');
    const products = (v: Vehicle) => v.assignedProducts > 0 ? (
        <Button variant="plain" onClick={() => onProducts(v)} accessibilityLabel={`Open assigned products for ${label(v)}`}>
            {v.assignedProducts.toLocaleString()}
        </Button>
    ) : <Text as="span" tone="subdued">None</Text>;

    return <>{(groups ?? vehicles.map(v => [v.id])).map(ids => {
        const records = ids.map(id => byId.get(id)).filter((v): v is { vehicle: Vehicle; index: number } => !!v);
        if (!records.length) return null;
        const first = records[0];
        const v = first.vehicle;
        const multiple = records.length > 1;
        const key = `vehicle-years-${Math.min(...ids)}`;
        const open = expanded.has(key);
        const ExpandIcon = open ? ChevronUpIcon : ChevronDownIcon;
        const selectedCount = records.filter(r => selected.includes(String(r.vehicle.id))).length;
        const years = records.map(r => value(r.vehicle, yearField));
        const sameCount = new Set(years).size === records.length
            && records.every(r => r.vehicle.assignedProducts === v.assignedProducts);
        const toggle = () => onToggle(key);
        return <React.Fragment key={key}>
            <IndexTable.Row id={multiple ? key : String(v.id)} position={first.index}
                selected={selectedCount === records.length ? true : selectedCount ? 'indeterminate' : false}
                selectionRange={multiple ? [first.index, records[records.length - 1].index] : undefined}
                accessibilityLabel={`Select ${label(v)}${multiple ? `, all ${records.length} years` : ''}`}>
                {columns.map((field, columnIndex) => {
                    const fieldKey = normalizeFitmentKey(field);
                    const identity = combined && fieldKey === 'model';
                    return <IndexTable.Cell key={fieldKey} className={columnIndex === 0 ? 'pf-vehicle-cell--identity' : 'pf-vehicle-cell--field'}>
                        <div className={identity ? 'pf-vehicle-identity' : 'pf-vehicle-value'}>
                            {!identity && <span className="pf-vehicle-mobile-label">{field.label}</span>}
                            <Text as="span" fontWeight={identity ? 'semibold' : 'regular'}>
                                {fieldKey === 'year' && multiple ? formatVehicleYears(years) : value(v, field) || '—'}
                            </Text>
                            {identity && <Text as="span" tone="subdued">{value(v, makeField) || '—'}</Text>}
                        </div>
                    </IndexTable.Cell>;
                })}
                <IndexTable.Cell className="pf-vehicle-cell--products">
                    <span className="pf-vehicle-mobile-label">Assigned products</span>
                    {!multiple ? products(v) : <button className="pf-vehicle-link" onClick={event => { event.stopPropagation(); toggle(); }}
                        aria-expanded={open} aria-controls={open ? key + '-details' : undefined}>
                        {sameCount ? `${v.assignedProducts.toLocaleString()} per year` : 'View by year'}
                    </button>}
                </IndexTable.Cell>
                <IndexTable.Cell className="pf-vehicle-cell--actions">
                    {multiple ? <button className="pf-vehicle-link pf-vehicle-expand" onClick={event => { event.stopPropagation(); toggle(); }}
                        aria-expanded={open} aria-controls={open ? key + '-details' : undefined}
                        aria-label={`${open ? 'Hide' : 'View'} years for ${label(v)}`}>
                        <span>{open ? 'Hide years' : 'View years'}</span>
                        <ExpandIcon width={16} height={16} aria-hidden="true" focusable="false" />
                    </button> : <Button variant="plain" onClick={() => onEdit(v)}>Edit</Button>}
                </IndexTable.Cell>
            </IndexTable.Row>
            {multiple && open && <IndexTable.Row id={key + '-details'} position={first.index} hideSelectable>
                <IndexTable.Cell colSpan={columns.length + 2}>
                    <div className="pf-vehicle-years" role="group" aria-label={`Individual years for ${label(v)}`}>
                        <div className="pf-vehicle-year-line pf-vehicle-year-heading"><span /><span>Year</span><span>Assigned products</span><span /></div>
                        {[...records].sort((a, b) => Number(value(b.vehicle, yearField)) - Number(value(a.vehicle, yearField))).map(({ vehicle }) => (
                            <div className="pf-vehicle-year-line" key={vehicle.id}>
                                <Checkbox label={`Select ${label(vehicle)}`} labelHidden checked={selected.includes(String(vehicle.id))}
                                    onChange={checked => onSelect(String(vehicle.id), checked)} />
                                <span>{value(vehicle, yearField) || '—'}</span>
                                <span>{products(vehicle)}</span>
                                <Button variant="plain" onClick={() => onEdit(vehicle)} accessibilityLabel={`Edit ${label(vehicle)}`}>Edit</Button>
                            </div>
                        ))}
                    </div>
                </IndexTable.Cell>
            </IndexTable.Row>}
        </React.Fragment>;
    })}</>;
}
